import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {AmpWSMessageEnum, isWSMessageEnum, LanguageShortEnum} from 'src/enums';
import {
    CHARGE_POINT_STATUS_TYPE,
    CHARGING_STATUS_TYPE,
    CONFIGURATIONS_TYPE,
    CONNECTOR_STATUS_TYPE,
    ContractDTO,
    UserAvatar,
    UserDTO,
} from 'src/types';
import {MePermissionsDTO} from 'src/types';
import {AuthUserDTO} from 'src/types/auth';
import {destinctSliceCollection} from 'src/utils/helper';
import {RootState} from '../../app/store';

export interface CommonState {
    lang: string;
    /**
     * What the server says this user may do. Read through the useCan hooks and nothing else:
     * the CASL layer that used to sit on top of this was removed, because a rule keyed on a subject
     * name in the database could evaluate false while the permission itself was plainly held, and
     * that failure is silent - the control simply does not render.
     *
     * Kept beside authInfo rather than derived from it: the roles claim is on its way out, and every
     * gate in the UI has to stop depending on the role name before a business can invent one.
     */
    access: MePermissionsDTO | null;
    // eslint-disable-next-line @typescript-eslint/ban-types
    authInfo: AuthUserDTO | {};
    isLoaded: boolean;
    activeBusiness: number;
    CHARGE_POINT_STATUS: CHARGE_POINT_STATUS_TYPE[];
    CHARGING_STATUS: CHARGING_STATUS_TYPE[];
    CONNECTOR_STATUS: CONNECTOR_STATUS_TYPE[];
    CONFIGURATIONS: CONFIGURATIONS_TYPE[];
}

const initialState: CommonState = {
    lang: LanguageShortEnum.EN,
    access: null,
    authInfo: {},
    isLoaded: false,
    // AMP IT. Only the platform administrator ever works outside their own business, and they have
    // none of their own - so something has to be the business they land in, and the first one is the
    // least surprising. Everyone else has this overwritten with their own at sign-in.
    activeBusiness: 1,
    CHARGE_POINT_STATUS: [],
    CHARGING_STATUS: [],
    CONFIGURATIONS: [],
    CONNECTOR_STATUS: [],
};

/**
 * News about the device itself: one row per charger.
 *
 * Generic on purpose. Typed as `(row: {chargerId: number}) => string` it becomes the best candidate
 * TypeScript has for the collection's element type, and every one of these assignments then fails.
 */
const byCharger = <T extends {chargerId: number}>({chargerId}: T) => String(chargerId);

/**
 * News a connector reports: one row per connector.
 *
 * `connectorId` here is the connector's **database id**, which is what the server puts in the
 * message - not the OCPP socket number printed on the station. Anything matching these rows to
 * something on screen must compare that id, and show the socket number.
 */
const byConnector = <T extends {chargerId: number; connectorId: number}>({
    chargerId,
    connectorId,
}: T) => `${chargerId}:${connectorId}`;

export const commonSlice = createSlice({
    name: 'basic',
    initialState,
    reducers: {
        changeLang: (state, action: PayloadAction<string>) => {
            state.lang = action.payload;
        },
        setAuthInfo: (state, action: PayloadAction<AuthUserDTO | UserDTO>) => {
            state.authInfo = action.payload;
        },
        clearAuthInfo: (state) => {
            state.authInfo = {};
            state.access = null;
        },
        setAccess: (state, action: PayloadAction<MePermissionsDTO | null>) => {
            state.access = action.payload;
        },
        preloaderChangeStatus: (state, action: PayloadAction<boolean>) => {
            state.isLoaded = action.payload;
        },
        setActiveBusiness: (state, action: PayloadAction<number>) => {
            state.activeBusiness = action.payload;
        },
        /**
         * Every live message the server sends, filed by subject.
         *
         * The two charger channels hold one row per charger. The two connector channels hold one row
         * per connector, which is the part that used to be wrong: filed by charger, a two-socket
         * station reporting both sockets overwrote itself on every message, so a card could only ever
         * show one of two running sessions - and not reliably the same one.
         */
        webSocketMessage: (state, action: PayloadAction<string>) => {
            const {type, payload} = JSON.parse(action.payload);
            if (!isWSMessageEnum(type)) {
                return;
            }

            switch (type) {
                case AmpWSMessageEnum.CHARGE_POINT_STATUS:
                    state.CHARGE_POINT_STATUS = destinctSliceCollection(
                        state.CHARGE_POINT_STATUS,
                        payload,
                        byCharger
                    );
                    break;
                case AmpWSMessageEnum.CONFIGURATIONS:
                    state.CONFIGURATIONS = destinctSliceCollection(
                        state.CONFIGURATIONS,
                        payload,
                        byCharger
                    );
                    break;
                case AmpWSMessageEnum.CHARGING_STATUS:
                    state.CHARGING_STATUS = destinctSliceCollection(
                        state.CHARGING_STATUS,
                        payload,
                        byConnector
                    );
                    break;
                case AmpWSMessageEnum.CONNECTOR_STATUS:
                    state.CONNECTOR_STATUS = destinctSliceCollection(
                        state.CONNECTOR_STATUS,
                        payload,
                        byConnector
                    );
                    break;
            }
        },
        updateContractStatus: (state, action: PayloadAction<ContractDTO>) => {
            state.authInfo = {
                ...state.authInfo,
                contract: {
                    created: action.payload.created,
                    id: action.payload.id,
                    name: action.payload.name,
                    status: action.payload.status,
                },
            };
        },
        updateUserPhoto: (state, action: PayloadAction<UserAvatar>) => {
            state.authInfo = {
                ...state.authInfo,
                avatar: action.payload,
            };
        },
        clearStore: (state) => {
            state.activeBusiness = 1;
            state.CHARGE_POINT_STATUS = [];
            state.CHARGING_STATUS = [];
            state.CONNECTOR_STATUS = [];
            state.CONFIGURATIONS = [];
        },
    },
});

export const {
    changeLang,
    setAuthInfo,
    clearAuthInfo,
    setAccess,
    preloaderChangeStatus,
    updateContractStatus,
    setActiveBusiness,
    webSocketMessage,
    updateUserPhoto,
    clearStore,
} = commonSlice.actions;

export const selectLang = (state: RootState) => state.lang.value;

export const getAuthInfo = (state: RootState) => state.authInfo.value;

export const getActiveBusiness = (state: RootState) => state.authInfo.activeBusiness;

export default commonSlice.reducer;
